import { Alert, Col, Row, Spin, Switch, notification, theme } from "antd";
import React, { useCallback, useState } from "react";
import useColorChange from "../../hooks/useColorChange";
import { useSelector } from "react-redux";
import ThemeCard from "../../components/ThemeCard";
import ThemeDropDown from "../../components/ThemeDropDown";
import ActionButton from "../../components/ActionButton";
import { useEffect } from "react";
import APICall from "../../APICall";
import EndPoints from "../../APICall/EndPoints";
import ThemeInputNumber from "../../components/ThemeInputNumber/ThemeInputNumber";
import {
  options,
  offlineOptions,
  StyledTheme,
  TradeModalRadio,
  TradeModalRadioGroup,
  TradeModalDivider,
} from "./style";
import { useGetFilteredUsers } from "../../APICall/apiHooks";
import store from "../../store/store";
import ThemeInput from "../../components/ThemeInput";

export const isValidTrade = (tradeObj, record) => {
  const isNsequeOrCrypto =
    record?.marketName === "NSE EQU" || record?.marketName === "CRYPTO";
  const isValidLot = isNsequeOrCrypto || tradeObj.lot > 0;
  const isValidQuantity = tradeObj?.quantity > 0;
  const isValidPrice = tradeObj?.tradePrice > 0;

  const isAllValid = isValidLot && isValidQuantity && isValidPrice;

  if (!isAllValid) {
    if (!isValidLot) {
      return [isAllValid, "Lot"];
    } else if (!isValidQuantity) {
      return [isAllValid, "Quantity"];
    } else if (!isValidPrice) {
      return [
        isAllValid,
        `Price (if you have open position in ${record?.symbolName} then, go to trades->position->exit to close position)`,
      ];
    }
  }

  return [isAllValid, null];
};

const ModalView = ({ modalType, record, onClose, activeScript }) => {
  const { marketName } = record;
  
  //state
  const [tradeType, setTradeType] = useState("market");
  const [type, setType] = useState(modalType === "BuyPrice" ? 1 : 2);
  const [tradeLoading, setTradeLoading] = useState(false);
  const [offlineTrade, setOfflineTrade] = useState(false);
  const [price, setPrice] = useState(0);
  const [priceError, setPriceError] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [contractExpiry, setContractExpiry] = useState(null);
  const [value, setValue] = useState({
    lot: 1,
    qty: record?.lotSize || 1,
  });
  const [preValue, setPreValue] = useState({ lot: 1, qty: record?.lotSize });
  const showLot =
    marketName === "NSE EQU" || marketName === "CRYPTO" ? false : true;

  // redux
  const userDetails = useSelector((state) => state.authSlice);
  // const data = useSelector(
  //   (state) => state?.webSocketSlice?.webSocket?.data?.[record?.symbol] || 0
  // );
  const accountType = userDetails?.userDetail?.accountType;

  // custom hook for get filtered users
  const { filteredData, filterDataLoading } = useGetFilteredUsers({
    type: "customer",
    editable: accountType !== "customer",
  });

  // variable
  const lotSize = record?.lotSize || 1;
  const userId = userDetails?.userDetail?.userId;
  const userIP = userDetails?.user?.userIP || "";
  const allowofflineTrade = userDetails?.additionalSharing?.offlineTrade;
  // const valid = GlobleMarket.includes(marketName);
  const market =
    marketName === "FOREX" || marketName === "GLOBEX" ? "forex" : "other";

  const createTrade = async (tradeObj) => {
    setTradeLoading(true);
    try {
      let response = await APICall("post", EndPoints.createTrade, tradeObj);
      if (response?.status === 200) {
        notification.success({ message: response?.data?.message });
        setTradeLoading(false);
        onClose();
        setPrice(0);
      } else {
        notification.error({ message: response?.data?.message, duration: 10 });
      }
    } catch (error) {
      notification.error({ message: error?.message });
    } finally {
      setTradeLoading(false);
    }
  };

  // life cycle
  useEffect(() => {
    if (accountType === "customer") {
      setCustomerId(userId);
    }
  }, []);

  // Fetch MCX tender date when marketName is MCX
  useEffect(() => {
    const fetchMcxTenderDate = async () => {
      if (marketName === "MCX" && record?.symbol) {
        try {
          const response = await APICall(
            "get",
            `${EndPoints.getMcxTenderDate}?symbol=${record.symbol}`
          );
          if (response?.status === 200 && response?.data?.data?.contractExpiry) {
            setContractExpiry(response.data.data.contractExpiry);
          }
        } catch (error) {
          // Silently handle error
          console.error("Error fetching MCX tender date:", error);
        }
      } else {
        setContractExpiry(null);
      }
    };

    fetchMcxTenderDate();
  }, [marketName, record?.symbol]);

  // Removed AntD Form sync to keep ThemeInput behavior

  useEffect(() => {
    if (modalType) {
      setType(modalType === "BuyPrice" ? 1 : 2);
    }
  }, [modalType]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "+") {
        setType(1);
      } else if (e.key === "-") {
        setType(2);
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [type]);

  // function
  const handleSubmit = useCallback(async () => {
    if (customerId === "") {
      return notification.error({ message: "Please fill CustomerId" });
    }
    if (!activeScript?.[record?.symbol] && !offlineTrade) {
      return notification.error({ message: "This script is not active" });
    }
    let marketCheck = marketName === "NSE EQU" || marketName === "NSE FUT";
    let limitTrade = tradeType === "limit" || tradeType === "intradaylimit";

    const scriptPrice =
      store.getState()?.webSocketSlice?.webSocket?.data?.[record?.symbol] || {};

    if (limitTrade && price >= scriptPrice?.Ask && price <= scriptPrice?.Bid) {
      return notification.error({
        message: "Limit order not allow between buy price and sell price",
      });
    }

    // Use unified validator to block invalid limit prices (keeps UI consistent)
    if (limitTrade && !offlineTrade) {
      const { ok, msg } = validateLimitPrice(price);
      if (!ok) {
        return notification.error({ message: msg || "Invalid price" });
      }
    }

    let tradeObj = {
      tradeId: userId,
      customerId: customerId,
      tradeIpAddress: userIP || 0,
      marketName: marketName || null,
      symbolName: record?.symbolName || null,
      symbol: record?.symbol || null,
      expiryDate: record?.expiryDate || null,
      lot: !showLot ? 0 : Number(value.lot),
      lotSize: !showLot ? 1 : record?.lotSize,
      ltp: scriptPrice?.LTP,
      quantity: Number(value.qty),
      title: tradeType,
      ordertype:
        tradeType === "market" || tradeType === "limit" || tradeType === "C/F" || tradeType === "bonus"
          ? "market"
          : "intraday",
      stoploss:
        marketName === "GLOBEX" || marketName === "FOREX"
          ? type == 1
            ? scriptPrice?.Bid
            : scriptPrice?.Ask
          : scriptPrice?.LTP || 0,
      takeprofit: null,
      tradePattern: type === 1 ? "buy" : "sell",
      tradePrice:
        (tradeType === "market" || tradeType === "intraday") && !offlineTrade
          ? type == 1
            ? scriptPrice?.Bid
              ? scriptPrice?.Bid
              : 0
            : scriptPrice?.Ask
            ? scriptPrice?.Ask
            : 0
          : Number(price),
      high: scriptPrice?.High,
      low: scriptPrice?.Low,
      strikePrice: record?.strikePrice || null,
      series: record?.series || null,
      marketStatus: offlineTrade === true ? "offline" : "online",
      tradeTime: new Date(),
      deviceType: "web",
    };

    if (tradeObj?.stoploss == 0) {
      return notification.error({ message: "LTP/Quotation not found" });
    }

    const [allValid, name] = isValidTrade(tradeObj, record);
    if (!allValid) {
      notification.error({
        message: `Please enter positive numbers greater than 0 for ${name}`,
      });
    } else {
      createTrade(tradeObj);
    }
  }, [
    customerId,
    marketName,
    record,
    showLot,
    tradeType,
    type,
    value,
    price,
    offlineTrade,
  ]);

  const onChangeTradeType = ({ target: { value } }) => {
    setTradeType(value);
    if (value === "limit" || value === "intradaylimit") {
      setPrice(0);
    } else if (value === "bonus") {
      setPrice(0.000001);
    } else {
      const data =
        store.getState()?.webSocketSlice?.webSocket?.data?.[record?.symbol] ||
        {};
      setPrice(
        type == 1 ? (data?.Bid ? data?.Bid : 0) : data?.Ask ? data?.Ask : 0
      );
    }
  };

  const onChange = ({ target: { value } }) => {
    setType(value);
  };

  const onChangeValue = ({ target: { name, value } }) => {
    if (name === "lot") {
      setValue({ lot: value, qty: lotSize * (value || 1) });
      setPreValue({ lot: value, qty: lotSize * (value || 1) });
    } else {
      setValue({ qty: value, lot: (value || 1) / lotSize });
      setPreValue({ qty: value, lot: (value || 1) / lotSize });
    }
  };

  // Submit-time validator for limit price to block invalid orders
  const validateLimitPrice = (raw) => {
    const isLimitType = tradeType === "limit" || tradeType === "intradaylimit";
    const isTargetMarket = ["MCX", "NSE EQU", "NSE FUT"].includes(marketName);
    if (!isLimitType || !isTargetMarket) return { ok: true };

    const input = (raw ?? "").toString();
    if (input === "") return { ok: false, msg: "Price is required" };
    const validFormat = /^\d*\.?\d{0,2}$/.test(input);
    const isEmptyOrValid = validFormat || input === "";

    if (marketName === "MCX") {
      if (parseFloat(input) > 1500 && isEmptyOrValid) return { ok: true };
      const decimal = input.split(".")[1]?.toString();
      if (!decimal || decimal.length === 1 || (decimal.length === 2 && (decimal[1] === "0" || decimal[1] === "5"))) {
        return { ok: true };
      }
      return { ok: false, msg: "MCX prices must be X, X.X, X.X0, or X.X5" };
    }

    const isIndex = ["BANKNIFTY", "NIFTY", "FINNIFTY", "MIDCPNIFTY", "NIFTYNXT50"].includes(record?.symbolName);
    const priceNum = parseFloat(input);
    if (!Number.isFinite(priceNum) || !isEmptyOrValid) {
      return { ok: false, msg: "Invalid price format" };
    }
    const getEquityTick = (p) => {
      if (p < 250) return 0.01;
      if (p <= 1000) return 0.05;
      if (p <= 5000) return 0.1;
      if (p <= 10000) return 0.5;
      if (p <= 20000) return 1.0;
      return 5.0;
    };
    const getIndexTick = (p) => {
      if (p <= 15000) return 0.05;
      if (p <= 30000) return 0.1;
      return 0.2;
    };
    const tick = isIndex ? getIndexTick(priceNum) : getEquityTick(priceNum);
    const [intPart, decPartRaw] = input.split(".");
    const decPart = decPartRaw ?? "";
    if (Number.isInteger(tick)) {
      if (decPart.length > 0) return { ok: false, msg: `Enter multiples of ${tick} (no decimals)` };
      const intVal = parseInt(intPart || "0", 10);
      if (tick === 5 && intVal % 5 !== 0) return { ok: false, msg: "Enter multiples of 5 (e.g., 5, 10, 15)" };
      return { ok: true };
    } else {
      const decimalsInTick = ("" + tick).split(".")[1]?.length ?? 0;
      if (decPart.length > decimalsInTick + 1) return { ok: false, msg: `Max ${decimalsInTick} decimal places` };
      if (decPart.length === decimalsInTick + 1 && decPart[decPart.length - 1] !== "0") {
        return { ok: false, msg: `Max ${decimalsInTick} decimal places` };
      }
      const effectiveDec =
        decPart.length >= decimalsInTick
          ? decPart.slice(0, decimalsInTick)
          : decPart.padEnd(decimalsInTick, "0");
      const paddedDec = effectiveDec;
      const scaledPrice = parseInt((intPart + paddedDec) || "0", 10);
      const scaledTick = Math.round(tick * Math.pow(10, decimalsInTick));
      if (scaledPrice % scaledTick === 0) return { ok: true };
      return { ok: false, msg: `Enter value in multiples of ${tick}` };
    }
  };

  // Create validation rules for price field
  const getPriceValidationRules = () => {
    const isLimitType = tradeType === "limit" || tradeType === "intradaylimit";
    const isFreeMarket = !["MCX", "NSE EQU", "NSE FUT"].includes(marketName);
    
    if (!isLimitType || isFreeMarket) {
      return [];
    }

    return [
      {
        validator: (_, value) => {
          if (!value || value === "") {
            return Promise.resolve();
          }

          const input = value.toString();
          const validFormat = /^\d*\.?\d{0,2}$/.test(input);
          const isEmptyOrValid = validFormat || input === "";

          // MCX rules
          if (marketName === "MCX") {
            if (parseFloat(input) > 1500 && isEmptyOrValid) {
              return Promise.resolve();
            }
            const decimal = input.split(".")[1]?.toString();
            if (
              !decimal ||
              decimal.length === 1 ||
              (decimal.length === 2 && (decimal[1] === "0" || decimal[1] === "5"))
            ) {
              return Promise.resolve();
            }
            return Promise.reject(new Error("MCX prices must be in format: X, X.X, X.X0, or X.X5"));
          }

          // NSE EQU / NSE FUT rules with tick-size validation
          const isIndex = [
            "BANKNIFTY",
            "NIFTY",
            "FINNIFTY",
            "MIDCPNIFTY",
            "NIFTYNXT50",
          ].includes(record?.symbolName);

          const priceNum = parseFloat(input);
          if (!Number.isFinite(priceNum) || !isEmptyOrValid) {
            return Promise.reject(new Error("Invalid price format"));
          }

          const getEquityTick = (p) => {
            if (p < 250) return 0.01;
            if (p <= 1000) return 0.05;
            if (p <= 5000) return 0.1;
            if (p <= 10000) return 0.5;
            if (p <= 20000) return 1.0;
            return 5.0;
          };

          const getIndexTick = (p) => {
            if (p <= 15000) return 0.05;
            if (p <= 30000) return 0.1;
            return 0.2;
          };

          const tick = isIndex ? getIndexTick(priceNum) : getEquityTick(priceNum);
          const [intPart, decPartRaw] = input.split(".");
          const decPart = decPartRaw ?? "";

          if (Number.isInteger(tick)) {
            // No decimals allowed when tick is whole number
            if (decPart.length > 0) {
              return Promise.reject(new Error(`Enter value in multiples of ${tick} (no decimals allowed)`));
            }
            const intVal = parseInt(intPart || "0", 10);
            if (tick === 5 && intVal % 5 !== 0) {
              return Promise.reject(new Error("Enter value in multiples of 5 (e.g., 5, 10, 15, 20...)"));
            }
            return Promise.resolve();
          } else {
            const decimalsInTick = ("" + tick).split(".")[1]?.length ?? 0;
            if (decPart.length > decimalsInTick ) {
              return Promise.reject(new Error(`Enter value in multiples of ${tick} (max ${decimalsInTick} decimal places)`));
            }

            const paddedDec = decPart.padEnd(decimalsInTick, "0");
            const scaledPrice = parseInt((intPart + paddedDec) || "0", 10);
            const scaledTick = Math.round(tick * Math.pow(10, decimalsInTick));
            if (scaledPrice % scaledTick === 0) {
              return Promise.resolve();
            } else {
              return Promise.reject(new Error(`Enter value in multiples of ${tick}`));
            }
          }
        },
      },
    ];
  };

  const onChangePrice = ({ target: { value } }) => {
    // Soft validation while typing; strict check occurs on submit
    const input = value ?? "";
    const validFormat = /^\d*\.?\d{0,2}$/.test(input);
    const isEmptyOrValid = validFormat || input === "";

    const isLimitType = tradeType === "limit" || tradeType === "intradaylimit";
    const isFreeMarket = !["MCX", "NSE EQU", "NSE FUT"].includes(marketName);

    if (isFreeMarket || !isLimitType) {
      setPriceError("");
      setPrice(input);
      return;
    }

    if (marketName === "MCX") {
      if (parseFloat(input) > 1500 && isEmptyOrValid) {
        setPriceError("");
        setPrice(parseFloat(input) + "");
        return;
      }
      const decimal = input.split(".")[1]?.toString();
      const ok = !decimal || decimal.length === 1 || (decimal.length === 2 && (decimal[1] === "0" || decimal[1] === "5"));
      setPrice(input);
      setPriceError(ok ? "" : "MCX prices must be X, X.X, X.X0, or X.X5");
      return;
    }

    // NSE EQU / FUT live tick validation when format is acceptable and not empty
    setPrice(input);
    if (input === "") {
      setPriceError("");
      return;
    }
    if (!isEmptyOrValid) {
      setPriceError("Invalid price format");
      return;
    }

    const isIndex = ["BANKNIFTY", "NIFTY", "FINNIFTY", "MIDCPNIFTY", "NIFTYNXT50"].includes(record?.symbolName);
    const priceNum = parseFloat(input);
    if (!Number.isFinite(priceNum)) {
      setPriceError("Invalid price format");
      return;
    }

    const getEquityTick = (p) => {
      if (p < 250) return 0.01;
      if (p <= 1000) return 0.05;
      if (p <= 5000) return 0.1;
      if (p <= 10000) return 0.5;
      if (p <= 20000) return 1.0;
      return 5.0;
    };
    const getIndexTick = (p) => {
      if (p <= 15000) return 0.05;
      if (p <= 30000) return 0.1;
      return 0.2;
    };

    const tick = isIndex ? getIndexTick(priceNum) : getEquityTick(priceNum);
    const [intPart, decPartRaw] = input.split(".");
    const decPart = decPartRaw ?? "";

    if (Number.isInteger(tick)) {
      if (decPart.length > 0) {
        setPriceError(`Enter value in multiples of ${tick} (no decimals allowed)`);
        return;
      }
      const intVal = parseInt(intPart || "0", 10);
      if (tick === 5 && intVal % 5 !== 0) {
        setPriceError("Enter value in multiples of 5 (e.g., 5, 10, 15, 20...)");
        return;
      }
      setPriceError("");
      return;
    } else {
      const decimalsInTick = ("" + tick).split(".")[1]?.length ?? 0;
      // Allow at most required decimals, or one extra trailing zero (e.g., 0.20 when decimalsInTick=1)
      if (decPart.length > decimalsInTick + 1) {
        setPriceError(`Enter value in multiples of ${tick} (max ${decimalsInTick} decimal places)`);
        return;
      }
      if (decPart.length === decimalsInTick + 1 && decPart[decPart.length - 1] !== "0") {
        setPriceError(`Enter value in multiples of ${tick} (max ${decimalsInTick} decimal places)`);
        return;
      }
      // Use the first required decimals for multiple calculation, ignore extra trailing zero
      const effectiveDec =
        decPart.length >= decimalsInTick
          ? decPart.slice(0, decimalsInTick)
          : decPart.padEnd(decimalsInTick, "0");
      const paddedDec = effectiveDec;
      const scaledPrice = parseInt((intPart + paddedDec) || "0", 10);
      const scaledTick = Math.round(tick * Math.pow(10, decimalsInTick));
      if (scaledPrice % scaledTick === 0) {
        setPriceError("");
      } else {
        setPriceError(`Enter value in multiples of ${tick}`);
      }
      return;
    }
  };

  const offlineTradeHandler = (item) => {
    setOfflineTrade(item);

    if (item) {
      if (tradeType === "limit" || tradeType === "intradaylimit") {
        setTradeType("market");
      }
      const data =
        store.getState()?.webSocketSlice?.webSocket?.data?.[record?.symbol] ||
        {};
      setPrice(
        type == 1 ? (data?.Bid ? data?.Bid : 0) : data?.Ask ? data?.Ask : 0
      );
    }
  };

  const onBlurPrice = () => {
    let checkPrice = Number(price);
    if (price === "" || !checkPrice) {
      setPrice(0);
    }
  };

  const onFocusPrice = () => {
    if (tradeType !== "bonus") {
      setPrice("");
    }
  };

  const onChangeDropDown = (value) => {
    let stringValue = value[0];
    setCustomerId(stringValue);
  };
  const onBlur = ({ target: { name, value } }) => {
    if (!value) {
      setValue({
        lot: preValue.lot,
        qty: preValue.qty,
      });
    }
  };

  const onFocus = ({ target: { name, value } }) => {
    const numberValue = parseFloat(value.replace(/,/g, ""));
    setPreValue((pre) => ({ ...pre, [name]: numberValue }));
    setValue((pre) => ({ ...pre, [name]: null }));
  };

  return (
    <StyledTheme.Root>
      <StyledTheme.Modal
        open={true}
        onOk={onClose}
        onCancel={onClose}
        $type={type}
      >
        {accountType !== "customer" && allowofflineTrade && (
          <Row style={{ display: "flex", justifyContent: "flex-end" }}>
            <Switch
              checkedChildren="Offline Trade"
              unCheckedChildren="Online Trade"
              onChange={offlineTradeHandler}
            />
          </Row>
        )}
        <Row
          justify={"space-evenly"}
          gutter={[5, 10]}
          style={{ marginTop: "1rem" }}
        >
          <Col xs={12} sm={8} md={6} lg={4} xl={3}>
            <ModalCellTrade
              title={`${record?.symbolName} ${record?.strikePrice || ""} ${
                record?.option_type || ""
              }  ${record?.expiryDate || ""}`}
              hideValue
            />
          </Col>

          <TradeModalValues
            record={record}
            name="SELL RATE"
            type="Ask"
            update
          />
          <TradeModalValues record={record} name="BUY RATE" type="Bid" update />
          <TradeModalValues
            record={record}
            update
            name={market === "forex" ? "SPREAD" : "LTP"}
            type={market === "forex" ? "Spread" : "LTP"}
          />
          <TradeModalValues record={record} name="VOLUME" type="Volume" />
          <TradeModalValues
            record={record}
            name="CHANGE %"
            type="Price_Change"
            pr
          />
          <TradeModalValues record={record} name="NET CHG" type="Net_Change" />
          <TradeModalValues record={record} name="HIGH" type="High" />
          <TradeModalValues record={record} name="LOW" type="Low" />
          <TradeModalValues record={record} name="OPEN" type="Open" />
          <TradeModalValues record={record} name="CLOSE" type="Close" />
        </Row>
        <TradeModalDivider />
        <ThemeCard bordered={false} style={{ margin: "1rem 0rem" }}>
          <StyledTheme.BuySellWrapper gutter={[5, 1]} justify={"space-evenly"}>
            <StyledTheme.RadioDiv>
              <Col xs={24} sm={12} md={12} lg={12} xl={7}>
                <TradeModalRadioGroup value={type} onChange={onChange}>
                  <TradeModalRadio value={1}>Buy</TradeModalRadio>
                  <TradeModalRadio value={2}>Sell</TradeModalRadio>
                </TradeModalRadioGroup>
              </Col>
              <Col span={24}>
                <StyledTheme.MarketTypeRadio
                  options={offlineTrade ? offlineOptions : options}
                  onChange={onChangeTradeType}
                  value={tradeType}
                  optionType="button"
                  buttonStyle="solid"
                />
              </Col>
            </StyledTheme.RadioDiv>

            {showLot && (
              <Col
                xs={24}
                sm={12}
                md={8}
                lg={6}
                xl={4}
                style={{ marginTop: "1rem" }}
              >
                <ThemeInputNumber
                  marginBottom={"0rem"}
                  width={"100%"}
                  title="LOT"
                  extraProps={{
                    input_type: "number",
                  }}
                  inputProps={{
                    placeholder: "LOT",
                    name: "lot",
                    value: value?.lot,
                    onChange: onChangeValue,
                    onBlur: onBlur,
                    onFocus: onFocus,
                  }}
                />
              </Col>
            )}
            <Col
              xs={24}
              sm={12}
              md={8}
              lg={6}
              xl={4}
              style={{ marginTop: "1rem" }}
            >
              <ThemeInputNumber
                marginBottom={"0rem"}
                width={"100%"}
                title="QTY"
                className="ThemeInput"
                extraProps={{
                  input_type: "number",
                }}
                inputProps={{
                  placeholder: "QTY",
                  name: "qty",
                  value: value?.qty,
                  onChange: onChangeValue,
                  onBlur: onBlur,
                  onFocus: onFocus,
                  // disabled: record?.market !== "NSE FUT",
                  locked: true,
                }}
              />
            </Col>
            <TradeModalPrice
              record={record}
              offlineTrade={offlineTrade}
              tradeType={tradeType}
              onChangePrice={onChangePrice}
              onBlurPrice={onBlurPrice}
              onFocusPrice={onFocusPrice}
              price={price}
              priceType={type === 1 ? "Bid" : "Ask"}
              priceError={priceError}
            />
            {accountType !== "customer" && (
              <Col xs={24} sm={12} md={8} lg={6} xl={4}>
                <ThemeDropDown
                  // marginBottom={"0rem"}
                  width={"100%"}
                  title={"Client"}
                  showSearch={true}
                  placeholder="Select Client"
                  options={filteredData}
                  value={customerId}
                  onChange={onChangeDropDown}
                  suffixIcon={
                    <Spin size="small" spinning={filterDataLoading} />
                  }
                />
              </Col>
            )}
          </StyledTheme.BuySellWrapper>

          {contractExpiry && (
            <Row style={{ marginTop: "1rem", marginBottom: "1rem" }}>
              <Col span={24}>
                <Alert
                  message={`This position will close on ${contractExpiry} as per last trading date.`}
                  type="warning"
                  closable={false}
                  style={{ backgroundColor: "red", color: "#fff",textAlign:"center" }}
                />
              </Col>
            </Row>
          )}

          <Row justify={"center"}>
            <ActionButton
              title={"Submit"}
              onClick={handleSubmit}
              loading={tradeLoading}
            />

            <ActionButton
              title={"Cancel"}
              buttonType="cancel"
              onClick={onClose}
            />
          </Row>
        </ThemeCard>
      </StyledTheme.Modal>
    </StyledTheme.Root>
  );
};

export default React.memo(ModalView);

const ModalCellTrade = React.memo(
  ({
    name = "",
    title,
    currentValue,
    checkUpdates = false,
    hideValue = false,
  }) => {
    const token = theme.useToken().token;
    const { color, backgroundColor } = useColorChange({
      currentValue: checkUpdates ? currentValue : null,
      defaultBgColor: token.colorBgBase,
      defaultColor: token.colorTextLabel,
    });

    return (
      <StyledTheme.Cell style={{ backgroundColor, border: "1px solid white" }}>
        <div className="cell-title" style={{ color }}>
          {title}
        </div>
        {hideValue ? null : (
          <div className="cell-number" style={{ color }}>
            {currentValue || "~"}
          </div>
        )}
      </StyledTheme.Cell>
    );
  }
);

const TradeModalValues = React.memo(({ name, type, record, update, pr }) => {
  const value =
    useSelector(
      (state) => state.webSocketSlice?.webSocket?.data?.[record?.symbol]?.[type]
    ) || 0;
  return (
    <Col xs={6} sm={6} md={4} lg={3} xl={2}>
      <ModalCellTrade
        title={name}
        currentValue={`${parseFloat(value)} ${pr ? "%" : ""}`}
        checkUpdates={update ? true : false}
      />
    </Col>
  );
});

const TradeModalPrice = React.memo(
  ({
    onChangePrice,
    onBlurPrice,
    onFocusPrice,
    price,
    record,
    offlineTrade,
    tradeType,
    priceType,
    priceError,
  }) => {
    const value =
      useSelector(
        (state) =>
          state.webSocketSlice?.webSocket?.data?.[record?.symbol]?.[priceType]
      ) || 0;
    return (
      <Col xs={24} sm={12} md={8} lg={6} xl={4} style={{ marginTop: "1rem" }}>
        <ThemeInput
          style={{ fontWeight: "bold" }}
          marginBottom={"0rem"}
          width={"100%"}
          title="PRICE"
          className="ThemeInput"
          formProps={{
            help: priceError || undefined,
            validateStatus: priceError ? "error" : undefined,
          }}
          extraProps={{
            input_type: "number",
          }}
          inputProps={{
            onChange: onChangePrice,
            onBlur: onBlurPrice,
            onFocus: onFocusPrice,
            readOnly:
              ((tradeType === "market" || tradeType === "intraday") &&
                !offlineTrade) ||
              tradeType === "bonus"
                ? "readOnly"
                : null,
            placeholder: "Price",
            value:
              (tradeType === "market" || tradeType === "intraday") &&
              !offlineTrade
                ? value
                : price,
          }}
        />
      </Col>
    );
  }
);
